'use client';

import { useAuth } from '@/backend/context/auth-management';
import { useMoney, useSale } from '@/backend/firebase/hooks';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ROUTES } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { MoneyUtils } from '@/shared/utils/money-utils';
import { WhatsAppUtils } from 'shared';
import {
  CheckCircle2,
  Copy,
  Loader2,
  MessageCircle,
  Receipt,
  ShieldCheck,
  Image as ImageIcon,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { apiClient } from '@/backend/api/api-client';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/badge';

export default function SaleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile, activeBusiness } = useAuth();
  const businessId = activeBusiness?.id || profile?.id || '';
  const businessSlug = activeBusiness?.public?.branding?.slug || profile?.public?.branding?.slug || '';
  const paymentMethods = activeBusiness?.public?.finance?.paymentMethods || profile?.public?.finance?.paymentMethods || [];

  const { data: sale, isLoading, refetch } = useSale.getById(id!, businessId);
  const { rates } = useMoney();

  const [isVerifying, setIsVerifying] = useState(false);
  const [isManualPaymentOpen, setIsManualPaymentOpen] = useState(false);
  const [manualPaymentMethodId, setManualPaymentMethodId] = useState('');

  const isPaid = sale?.status === 'paid';
  const isVerifyingPayment = sale?.status === 'verifying';
  const checkoutCode = sale?.checkout?.code || id?.slice(0, 6).toUpperCase();
  const publicBaseUrl = import.meta.env.VITE_NEXT_PUBLIC_URL || window.location.origin.replace('app.', '');
  const paymentLink = `${publicBaseUrl}/p/${checkoutCode}?type=sale`;
  const publicPaymentMethod = sale?.checkout?.paymentMethod;
  const manualPaymentMethod = paymentMethods.find((method: any) => method.id === manualPaymentMethodId);
  const selectedPaymentMethod = publicPaymentMethod || manualPaymentMethod;
  const activationToken = sale?.academy?.activationLinks?.[0]?.token;
  const activationLink = activationToken && businessSlug
    ? `${import.meta.env.VITE_NEXT_PUBLIC_URL || window.location.origin.replace('app.', '')}/${businessSlug}/academy/activate?token=${activationToken}`
    : '';

  const handleApprovePayment = async () => {
    if (!selectedPaymentMethod) {
      toast.error('Falta el método de pago');
      return;
    }

    setIsVerifying(true);
    try {
      // Single unified endpoint: verify-payment handles Sale update + Enrollment activation
      await apiClient.post('/api/academy/verify-payment', {
        ownerId: businessId,
        saleId: id,
        paymentMethod: selectedPaymentMethod,
        proofFileName: sale?.checkout?.proofFileName || null,
      });

      // Fire Meta CAPI Purchase event + update CRM lead status
      await apiClient.patch('/api/marketing/leads', {
        ownerId: businessId,
        leadId: sale?.client?.uid,
        status: 'SOLD',
        overrideValue: sale?.total_accounting,
        overrideCurrency: 'USD',
        saleId: id,
        courseIds: sale?.items?.map((item: any) => item.courseId).filter(Boolean) || [],
      });

      toast.success('¡Venta confirmada y acceso activado!');
      refetch?.();
    } catch (error) {
      toast.error('Error al verificar el pago');
    } finally {
      setIsVerifying(false);
    }
  };

  if (isLoading) return <div className="h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  if (!sale) return <div className="p-20 text-center font-black uppercase italic text-zinc-300">Venta no encontrada</div>;

  const proofDataUrl = sale?.checkout?.proofDataUrl;
  const proofFileName = sale?.checkout?.proofFileName;

  return (
    <div className="min-h-screen bg-zinc-50 pb-32 italic font-[var(--font-family)]">
      <PageHeader title="Comprobante de Venta" subtitle={isPaid ? 'Saldado' : isVerifyingPayment ? 'Comprobante recibido' : 'Pendiente'} showBack />

      <main className="mx-auto max-w-md px-4 space-y-6">
        {/* CLIENT & STATUS */}
        <div className="flex items-center justify-between px-2">
           <div className="flex items-center gap-3 text-left">
              <div className="h-10 w-10 bg-zinc-900 rounded-full flex items-center justify-center text-white font-black italic text-xs">
                {sale.client.name.charAt(0)}
              </div>
              <div>
                <p className="text-sm font-black uppercase italic tracking-tighter text-zinc-900">{sale.client.name}</p>
                <p className="text-[10px] font-bold text-zinc-400">{sale.client.phone}</p>
              </div>
           </div>
           <Badge className={cn(
             "px-3 py-1 rounded-full border-none font-black text-[9px] uppercase",
             isPaid ? "bg-emerald-500 text-white" :
             isVerifyingPayment ? "bg-purple-100 text-purple-700" :
             "bg-rose-100 text-rose-700"
           )}>
             {isPaid ? 'CONFIRMADO' : isVerifyingPayment ? 'POR CONFIRMAR' : 'PENDIENTE'}
           </Badge>
        </div>

        {/* RECEIPT CARD */}
        <Card className="rounded-[2.5rem] border-none bg-white shadow-xl shadow-zinc-200/50 overflow-hidden">
          <div className="bg-zinc-900 p-6 text-white flex items-center justify-between">
             <div className="flex items-center gap-2">
                <Receipt className="h-4 w-4 text-emerald-400" />
                <span className="text-[10px] font-black uppercase tracking-widest">Resumen de Venta</span>
             </div>
             <span className="text-[10px] font-black opacity-40">ID: {id?.substring(0,8)}</span>
          </div>
          <CardContent className="p-8 space-y-6">
            <div className="rounded-2xl bg-zinc-50 p-4 text-center">
              <p className="text-[8px] font-black uppercase tracking-widest text-zinc-400">Código corto de pago</p>
              <p className="mt-1 text-2xl font-black tracking-[0.25em] text-zinc-900">{checkoutCode}</p>
            </div>

            <div className="space-y-3">
               {sale.items.map((item, i) => (
                 <div key={i} className="flex justify-between items-center">
                    <span className="text-sm font-bold text-zinc-500 uppercase">{item.name}</span>
                    <span className="text-sm font-black text-zinc-900">${item.price}</span>
                 </div>
               ))}
            </div>

            <Separator className="bg-zinc-50" />

            <div className="flex flex-col items-end">
               <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-1">Total Cobrado</p>
               <p className={cn("text-5xl font-black italic tracking-tighter", isPaid ? "text-emerald-500" : "text-zinc-900")}>
                 {MoneyUtils.format(sale.total_accounting, 'USD')}
               </p>
            </div>
          </CardContent>
        </Card>

        {/* PROOF OF PAYMENT */}
        {!isPaid && (
          <section className="space-y-4">
             <div className="flex items-center gap-2 px-2 text-zinc-400">
                <ShieldCheck className="h-4 w-4" />
                <h2 className="text-[10px] font-black uppercase tracking-widest">Verificación de Pago</h2>
             </div>

             <Card className={cn(
               "rounded-[2.5rem] border-2 p-8 text-center transition-all",
               isVerifyingPayment ? "border-purple-200 bg-purple-50" : "border-zinc-100 bg-white"
             )}>
                {isVerifyingPayment ? (
                  <div className="space-y-6">
                    {proofDataUrl ? (
                      <div className="overflow-hidden rounded-[2rem] border-4 border-white bg-white shadow-xl">
                        <img src={proofDataUrl} alt="Comprobante de pago" className="max-h-80 w-full object-contain" />
                      </div>
                    ) : (
                      <div className="mx-auto h-20 w-20 bg-purple-100 rounded-[2rem] flex items-center justify-center">
                         <ImageIcon className="h-10 w-10 text-purple-600" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-black uppercase italic text-purple-900">Captura Recibida</h3>
                      <p className="text-xs font-medium text-purple-600/70">{proofFileName || 'El cliente ha subido un comprobante.'}</p>
                      {publicPaymentMethod && (
                        <p className="mt-2 text-[10px] font-black uppercase tracking-widest text-purple-900">
                          Metodo: {publicPaymentMethod.title}
                        </p>
                      )}
                    </div>
                    <Button
                      onClick={handleApprovePayment}
                      disabled={isVerifying || !selectedPaymentMethod}
                      className="h-16 w-full rounded-2xl bg-zinc-900 text-white font-black uppercase tracking-widest shadow-xl active:scale-95 border-none"
                    >
                      {isVerifying ? <Loader2 className="animate-spin" /> : "Confirmar Venta y CAPI"}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-xs font-medium text-zinc-400">Envía o copia el enlace. También puedes confirmar de una vez si ya verificaste el pago.</p>
                    <div className="rounded-2xl bg-zinc-50 p-3 text-center">
                      <p className="break-all text-[10px] font-bold text-zinc-500">{paymentLink}</p>
                    </div>
                    <Button
                      className="h-14 w-full rounded-[1.5rem] border-none bg-emerald-500 text-[10px] font-black uppercase tracking-widest text-white shadow-lg shadow-emerald-100"
                      onClick={() => {
                        WhatsAppUtils.redirectToWhatsApp(
                          sale.client.phone,
                          `Hola ${sale.client.name}, aquí tienes el enlace para completar el pago de ${MoneyUtils.format(sale.total_accounting, 'USD')} y subir el comprobante: ${paymentLink}`,
                        );
                      }}
                    >
                      <MessageCircle className="mr-2 h-5 w-5" /> Enviar Link por WhatsApp
                    </Button>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="outline"
                        className="h-12 rounded-[1.2rem] border-zinc-200 bg-white text-[9px] font-black uppercase tracking-widest text-zinc-600"
                        onClick={() => {
                          navigator.clipboard.writeText(paymentLink);
                          toast.success('Link copiado');
                        }}
                      >
                        <Copy className="mr-2 h-4 w-4" /> Copiar
                      </Button>
                      <Button
                        className="h-12 rounded-[1.2rem] bg-zinc-900 text-[9px] font-black uppercase tracking-widest text-white"
                        onClick={() => setIsManualPaymentOpen(!isManualPaymentOpen)}
                      >
                        Pago Manual
                      </Button>
                    </div>
                    {isManualPaymentOpen && (
                      <div className="space-y-3 rounded-[1.8rem] bg-zinc-50 p-4 text-left">
                        <Label className="ml-1 block text-[9px] font-black uppercase tracking-widest text-zinc-400">
                          Método recibido
                        </Label>
                        <select
                          value={manualPaymentMethodId}
                          onChange={(event) => setManualPaymentMethodId(event.target.value)}
                          className="h-12 w-full rounded-2xl border border-zinc-100 bg-white px-4 text-xs font-black uppercase text-zinc-700"
                        >
                          <option value="">Seleccionar método</option>
                          {paymentMethods.filter((method: any) => method.isActive !== false).map((method: any) => (
                            <option key={method.id} value={method.id}>
                              {method.title} ({method.currency})
                            </option>
                          ))}
                        </select>
                        <Button
                          className="h-12 w-full rounded-[1.2rem] bg-zinc-900 text-[9px] font-black uppercase tracking-widest text-white"
                          disabled={isVerifying || !manualPaymentMethod}
                          onClick={handleApprovePayment}
                        >
                          Confirmar Manual
                        </Button>
                      </div>
                    )}
                  </div>
                )}
             </Card>
          </section>
        )}

        {isPaid && (
          <div className="flex flex-col items-center gap-4 py-8 animate-in zoom-in-95">
             <div className="h-20 w-20 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 shadow-xl shadow-emerald-100">
                <CheckCircle2 className="h-10 w-10" />
             </div>
             <div className="text-center">
                <h3 className="text-2xl font-black uppercase italic text-zinc-900 tracking-tighter leading-none">Venta Finalizada</h3>
                <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mt-2">Evento Purchase enviado a Meta Ads</p>
             </div>
             <Button
               variant="ghost"
               className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-400 mt-4"
               onClick={() => navigate(ROUTES.DASHBOARD.PRINCIPAL)}
             >
               Volver al Centro de Clientes
             </Button>
             {activationLink && (
               <Button
                 className="h-14 w-full rounded-[1.5rem] bg-amber-500 text-[10px] font-black uppercase tracking-widest text-white shadow-lg shadow-amber-100"
                 onClick={() => {
                   WhatsAppUtils.redirectToWhatsApp(
                     sale.client.phone,
                     `¡Pago confirmado, ${sale.client.name}! Activa tu acceso al curso aquí: ${activationLink}`,
                   );
                 }}
               >
                 Enviar Acceso al Curso
               </Button>
             )}
          </div>
        )}
      </main>
    </div>
  );
}
